/**
 * Ride Revolt — Extended Warranty & Care Protection add-on
 *
 * Handles two things:
 *  1. Product page: checking the warranty checkbox adds the matched
 *     warranty variant to the cart IMMEDIATELY, as its own independent
 *     /cart/add.js call — it does NOT hook the bike's own Add to Cart
 *     button or form submission. Unchecking removes it the same way.
 *
 *     This is deliberate: this theme's product form is a custom
 *     <product-form> element (from the theme's own externally-hosted
 *     bundle) that already intercepts submit for its own AJAX add-to-cart
 *     flow. Also listening for that same submit event here would risk
 *     both handlers firing and adding the bike twice. Decoupling the
 *     warranty add from the bike's add-to-cart avoids that entirely, at
 *     the small cost that a shopper who checks the box and then never
 *     buys the bike is left with a lone warranty line in their cart
 *     (harmless — no charge happens unless they complete checkout, and
 *     they can remove it same as any other cart item).
 *
 *  2. Cart page: for any bike already in the cart without its warranty
 *     variant, renders a small "Add protection" offer and adds it on
 *     click (already a standalone /cart/add.js call, unaffected by any
 *     of the above).
 *
 * Include on both the product template and the cart template:
 *   {{ 'ride-revolt-warranty.js' | asset_url | script_tag }}
 */

(function () {
  function initProductPage() {
    var addon = document.querySelector('.rr-warranty-addon');
    if (!addon) return;

    var checkbox = addon.querySelector('.rr-warranty-addon__checkbox');
    var priceEl = addon.querySelector('.rr-warranty-addon__price');
    var form = addon.closest('form');
    var hiddenIdInput = form ? form.querySelector('input[name="id"]') : null;
    if (!checkbox) return;

    var variantMap = {};
    try {
      // The Liquid side outputs only the inner "key":value entries (no
      // surrounding braces — a literal '{' inside a quoted Liquid string
      // trips this theme's template validator), so wrap them here instead.
      var rawMap = addon.getAttribute('data-warranty-variant-map') || '';
      variantMap = JSON.parse('{' + rawMap + '}');
    } catch (e) {
      variantMap = {};
    }

    var currentWarranty = null; // { id, price } for the currently selected bike variant, or null
    var addedWarrantyVariantId = null; // warranty variant id actually sitting in the cart right now, or null

    // Queue cart requests so a rapid check/uncheck or a variant swap can't
    // race — each call waits for the previous one to settle first.
    var pending = Promise.resolve();

    function warrantyForVariant(variantId) {
      return (variantId && variantMap[variantId]) || null;
    }

    function applyDisplay(entry) {
      currentWarranty = entry;
      if (!entry) {
        addon.classList.add('rr-warranty-addon--hidden');
        return;
      }
      addon.classList.remove('rr-warranty-addon--hidden');
      if (priceEl) priceEl.textContent = entry.price;
    }

    function addWarrantyToCart(variantId) {
      pending = pending
        .then(function () {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });
        })
        .then(function (res) {
          if (!res.ok) throw new Error('Add to cart request failed');
          addedWarrantyVariantId = variantId;
        })
        .catch(function (err) {
          console.error('Ride Revolt warranty add-on: ', err);
          checkbox.checked = false; // reflect that it didn't actually get added
        });
      return pending;
    }

    function removeWarrantyFromCart(variantId) {
      if (!variantId) return pending;
      pending = pending
        .then(function () {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 0 })
          });
        })
        .then(function (res) {
          if (!res.ok) throw new Error('Remove from cart request failed');
          addedWarrantyVariantId = null;
        })
        .catch(function (err) {
          console.error('Ride Revolt warranty add-on: ', err);
        });
      return pending;
    }

    // Initial state, from the variant selected when the page rendered.
    applyDisplay(warrantyForVariant(hiddenIdInput ? hiddenIdInput.value : null));

    checkbox.addEventListener('change', function () {
      if (!currentWarranty) {
        checkbox.checked = false;
        return;
      }
      if (checkbox.checked) {
        addWarrantyToCart(currentWarranty.id);
      } else {
        removeWarrantyFromCart(addedWarrantyVariantId);
      }
    });

    // Re-check whenever the selected variant changes (e.g. single vs. dual
    // battery). Most themes swap the hidden `id` input's value via JS
    // rather than a real attribute change, so a lightweight poll is the
    // one approach that works regardless of theme. 300ms feels instant.
    //
    // If your theme dispatches its own event on variant change, you can
    // swap this poll for a listener on that event instead — call the same
    // logic below directly for a zero-delay update.
    if (hiddenIdInput) {
      var lastValue = hiddenIdInput.value;
      setInterval(function () {
        if (hiddenIdInput.value === lastValue) return;
        lastValue = hiddenIdInput.value;
        var newEntry = warrantyForVariant(lastValue);

        if (checkbox.checked && addedWarrantyVariantId) {
          // Swap: remove the old variant's warranty, add the new one (if any).
          removeWarrantyFromCart(addedWarrantyVariantId);
          if (newEntry) {
            addWarrantyToCart(newEntry.id);
          } else {
            checkbox.checked = false;
          }
        }

        applyDisplay(newEntry);
      }, 300);
    }
  }

  function initCartPage() {
    var dataEl = document.getElementById('rr-warranty-cart-data');
    var container = document.getElementById('rr-warranty-cart-upsell');
    if (!dataEl || !container) return;

    var offers;
    try {
      offers = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!offers || !offers.length) return;

    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var cartVariantIds = cart.items.map(function (item) {
          return item.variant_id;
        });

        var toOffer = offers.filter(function (offer) {
          return cartVariantIds.indexOf(offer.warranty_variant_id) === -1;
        });

        if (!toOffer.length) {
          container.innerHTML = '';
          return;
        }

        function escapeHtml(str) {
          var div = document.createElement('div');
          div.textContent = str == null ? '' : String(str);
          return div.innerHTML;
        }

        container.innerHTML = '';
        toOffer.forEach(function (offer) {
          var block = document.createElement('div');
          block.className = 'rr-warranty-cart-upsell__item';

          var iconHtml = offer.icon_url
            ? '<img src="' + escapeHtml(offer.icon_url) + '" alt="2-Year Warranty Protection" loading="lazy">'
            : '';

          block.innerHTML =
            '<div class="rr-warranty-cart-upsell__icon">' + iconHtml + '</div>' +
            '<div class="rr-warranty-cart-upsell__body">' +
              '<span class="rr-warranty-cart-upsell__title">2-Year Warranty Protection</span>' +
              '<span class="rr-warranty-cart-upsell__desc">For your ' + escapeHtml(offer.bike_title) +
              ' — <span class="rr-warranty-cart-upsell__price">' + escapeHtml(offer.price) + '</span></span>' +
              '<a class="rr-warranty-cart-upsell__learn-more" href="/pages/2-year-warranty-protection">Learn More</a>' +
            '</div>' +
            '<button type="button" class="rr-warranty-cart-upsell__button" data-variant-id="' +
            offer.warranty_variant_id + '">Add</button>';
          container.appendChild(block);
        });

        container.querySelectorAll('button[data-variant-id]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            btn.disabled = true;
            var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
            fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
            })
              .then(function (res) {
                if (!res.ok) throw new Error('Add to cart request failed');
                window.location.reload();
              })
              .catch(function (err) {
                console.error('Ride Revolt warranty add-on: ', err);
                btn.disabled = false;
              });
          });
        });
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initProductPage();
    initCartPage();
  });

  // The cart page re-renders its item list via AJAX on quantity/remove
  // changes (no full page reload), which wipes out this JS-populated
  // offer along with it. Re-run on every cart change so the offer
  // reliably reappears — e.g. add the warranty, then remove it, and it
  // sticks around as an option again instead of staying gone until a
  // manual refresh.
  document.addEventListener('cart:updated', initCartPage);
  document.addEventListener('cart-drawer:open', initCartPage);
})();
